import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Alert, Button, Spinner, Badge, Modal, Form,
  OverlayTrigger, Tooltip, ButtonGroup, Row, Col
} from 'react-bootstrap';
import {
  FaExclamationTriangle, FaCheckCircle, FaTimes, FaHistory,
  FaSearch, FaFilter, FaChevronLeft, FaChevronRight,
  FaExclamationCircle, FaInfoCircle, FaArrowUp, FaEye,
  FaBan, FaHandsHelping, FaGavel, FaClock, FaChartLine,
  FaTrash, FaCheck
} from 'react-icons/fa';
import knowledgeApi from '../../services/knowledgeApi';

const ConflictResolution = ({ setError, setSuccess }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total_filtered: 0, total_pages: 0 });
  const [filters, setFilters] = useState({
    severity: '',
    conflict_type: '',
    source_identifier: '',
    sort_by: 'priority'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [history, setHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState({});

  // Load dashboard
  const loadDashboard = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pagination.page_size,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await knowledgeApi.conflicts.dashboard(params);
      
      setStats(response.stats || {});
      setConflicts(response.conflicts || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Error loading conflicts:', error);
      setError('Failed to load conflicts: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page_size, setError]);

  // Load conflict history
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await knowledgeApi.conflicts.history({ days: 30, page: 1, page_size: 50 });
      setHistory(response.history || []);
    } catch (error) {
      console.error('Error loading conflict history:', error);
      setError('Failed to load history: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  // Load conflict detail
  const loadConflictDetail = useCallback(async (conflictId) => {
    try {
      const response = await knowledgeApi.conflicts.getConflict(conflictId);
      setSelectedConflict(response.conflict);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading conflict detail:', error);
      setError('Failed to load conflict details: ' + (error.message || 'Unknown error'));
    }
  }, [setError]);

  // Conflict resolution actions for inline buttons
  const handleKeepBoth = async (conflictId) => {
    if (!window.confirm('Mark both learnings as complementary?')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`keep_both_${conflictId}`]: true }));
      
      await knowledgeApi.conflicts.resolve({
        conflict_id: conflictId,
        action: 'resolve',
        resolution_type: 'keep_both'
      });
      
      setSuccess('Conflict resolved - both learnings kept');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error keeping both:', error);
      setError('Failed to keep both: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`keep_both_${conflictId}`]: false }));
    }
  };

  const handleDeleteFirst = async (conflictId, firstLearningId) => {
    if (!window.confirm('Archive the first learning and resolve the conflict?')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`delete_first_${conflictId}`]: true }));
      
      // Archive the first learning
      await knowledgeApi.write.archiveLearning(firstLearningId, 'conflict_resolution', 'user');
      
      // Mark conflict as resolved
      await knowledgeApi.conflicts.resolve({
        conflict_id: conflictId,
        action: 'resolve',
        resolution_type: 'archive_first'
      });
      
      setSuccess('First learning archived and conflict resolved');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error deleting first:', error);
      setError('Failed to delete first learning: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_first_${conflictId}`]: false }));
    }
  };

  const handleDeleteSecond = async (conflictId, secondLearningId) => {
    if (!window.confirm('Archive the second learning and resolve the conflict?')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`delete_second_${conflictId}`]: true }));
      
      // Archive the second learning
      await knowledgeApi.write.archiveLearning(secondLearningId, 'conflict_resolution', 'user');
      
      // Mark conflict as resolved
      await knowledgeApi.conflicts.resolve({
        conflict_id: conflictId,
        action: 'resolve',
        resolution_type: 'archive_second'
      });
      
      setSuccess('Second learning archived and conflict resolved');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error deleting second:', error);
      setError('Failed to delete second learning: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_second_${conflictId}`]: false }));
    }
  };

  const handleDeleteBoth = async (conflictId, firstLearningId, secondLearningId) => {
    if (!window.confirm('Archive both learnings and resolve the conflict? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`delete_both_${conflictId}`]: true }));
      
      // Archive both learnings
      await knowledgeApi.write.archiveLearning(firstLearningId, 'conflict_resolution', 'user');
      await knowledgeApi.write.archiveLearning(secondLearningId, 'conflict_resolution', 'user');
      
      // Mark conflict as resolved
      await knowledgeApi.conflicts.resolve({
        conflict_id: conflictId,
        action: 'resolve',
        resolution_type: 'archive_both'
      });
      
      setSuccess('Both learnings archived and conflict resolved');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error deleting both:', error);
      setError('Failed to delete both learnings: ' + (error.message || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_both_${conflictId}`]: false }));
    }
  };

  // Original modal resolution handlers
  const handleResolve = async (conflictId, winnerLearningId) => {
    if (!window.confirm('Are you sure you want to resolve this conflict? The losing learning will be archived.')) {
      return;
    }

    try {
      setResolving(true);
      await knowledgeApi.conflicts.resolve({
        conflict_id: conflictId,
        winner_learning_id: winnerLearningId,
        reviewed_by: 'user',
        notes: resolutionNotes
      });
      
      setSuccess('Conflict resolved successfully');
      setShowDetailModal(false);
      setResolutionNotes('');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error resolving conflict:', error);
      setError('Failed to resolve conflict: ' + (error.message || 'Unknown error'));
    } finally {
      setResolving(false);
    }
  };

  // Keep both learnings
  const handleBothKeep = async (conflictId) => {
    if (!window.confirm('Mark both learnings as complementary?')) {
      return;
    }

    try {
      setResolving(true);
      await knowledgeApi.conflicts.bothKeep({
        conflict_id: conflictId,
        reviewed_by: 'user',
        notes: resolutionNotes || 'Both learnings are complementary'
      });
      
      setSuccess('Conflict marked as complementary - both learnings kept');
      setShowDetailModal(false);
      setResolutionNotes('');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error keeping both:', error);
      setError('Failed to keep both: ' + (error.message || 'Unknown error'));
    } finally {
      setResolving(false);
    }
  };

  // Dismiss conflict
  const handleDismiss = async (conflictId) => {
    if (!window.confirm('Dismiss this conflict as a false positive?')) {
      return;
    }

    try {
      setResolving(true);
      await knowledgeApi.conflicts.dismiss({
        conflict_id: conflictId,
        reviewed_by: 'user',
        notes: resolutionNotes || 'False positive'
      });
      
      setSuccess('Conflict dismissed');
      setShowDetailModal(false);
      setResolutionNotes('');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error dismissing conflict:', error);
      setError('Failed to dismiss conflict: ' + (error.message || 'Unknown error'));
    } finally {
      setResolving(false);
    }
  };

  // Escalate conflict
  const handleEscalate = async (conflictId) => {
    try {
      setResolving(true);
      await knowledgeApi.conflicts.escalate({
        conflict_id: conflictId,
        priority: 100,
        notes: resolutionNotes || 'Escalated for expert review',
        escalated_by: 'user'
      });
      
      setSuccess('Conflict escalated for review');
      setShowDetailModal(false);
      setResolutionNotes('');
      loadDashboard(pagination.page);
    } catch (error) {
      console.error('Error escalating conflict:', error);
      setError('Failed to escalate conflict: ' + (error.message || 'Unknown error'));
    } finally {
      setResolving(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadDashboard, loadHistory]);

  // Apply filters
  const applyFilters = () => {
    loadDashboard(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      severity: '',
      conflict_type: '',
      source_identifier: '',
      sort_by: 'priority'
    });
  };

  // Severity badge
  const getSeverityBadge = (severity) => {
    const variants = {
      high: { bg: 'danger', icon: FaExclamationCircle },
      medium: { bg: 'warning', icon: FaExclamationTriangle },
      low: { bg: 'info', icon: FaInfoCircle }
    };
    const variant = variants[severity] || variants.low;
    const Icon = variant.icon;
    return (
      <Badge bg={variant.bg} className="d-flex align-items-center gap-1">
        <Icon size={10} /> {severity?.toUpperCase()}
      </Badge>
    );
  };

  // Stats Card
  const StatsCard = ({ icon: Icon, label, value, color }) => (
    <div className="cr-stat-card" style={{ borderLeftColor: color }}>
      <div className="cr-stat-icon" style={{ color }}>
        <Icon size={24} />
      </div>
      <div className="cr-stat-info">
        <div className="cr-stat-value">{value}</div>
        <div className="cr-stat-label">{label}</div>
      </div>
    </div>
  );

  // Action Buttons Component
  const ConflictActionButtons = ({ conflict }) => {
    const conflictId = conflict.conflict_id;
    const firstLearningId = conflict.learning_1?.learning_id;
    const secondLearningId = conflict.learning_2?.learning_id;

    return (
      <div className="cr-action-buttons d-flex gap-1 flex-wrap">
        <OverlayTrigger overlay={<Tooltip>Keep both learnings as complementary</Tooltip>}>
          <Button
            size="sm"
            variant="success"
            className="cr-action-btn cr-action-keep-both"
            onClick={(e) => {
              e.stopPropagation();
              handleKeepBoth(conflictId);
            }}
            disabled={actionLoading[`keep_both_${conflictId}`]}
          >
            {actionLoading[`keep_both_${conflictId}`] ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FaCheck size={10} className="me-1" />
                Keep Both
              </>
            )}
          </Button>
        </OverlayTrigger>

        <OverlayTrigger overlay={<Tooltip>Archive first learning</Tooltip>}>
          <Button
            size="sm"
            variant="warning"
            className="cr-action-btn cr-action-delete-first"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteFirst(conflictId, firstLearningId);
            }}
            disabled={actionLoading[`delete_first_${conflictId}`]}
          >
            {actionLoading[`delete_first_${conflictId}`] ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FaTrash size={10} className="me-1" />
                Delete 1st
              </>
            )}
          </Button>
        </OverlayTrigger>

        <OverlayTrigger overlay={<Tooltip>Archive second learning</Tooltip>}>
          <Button
            size="sm"
            variant="warning"
            className="cr-action-btn cr-action-delete-second"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSecond(conflictId, secondLearningId);
            }}
            disabled={actionLoading[`delete_second_${conflictId}`]}
          >
            {actionLoading[`delete_second_${conflictId}`] ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FaTrash size={10} className="me-1" />
                Delete 2nd
              </>
            )}
          </Button>
        </OverlayTrigger>

        <OverlayTrigger overlay={<Tooltip>Archive both learnings</Tooltip>}>
          <Button
            size="sm"
            variant="danger"
            className="cr-action-btn cr-action-delete-both"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteBoth(conflictId, firstLearningId, secondLearningId);
            }}
            disabled={actionLoading[`delete_both_${conflictId}`]}
          >
            {actionLoading[`delete_both_${conflictId}`] ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FaTrash size={10} className="me-1" />
                Delete Both
              </>
            )}
          </Button>
        </OverlayTrigger>
      </div>
    );
  };

  // Conflict Card
  const ConflictCard = ({ conflict }) => (
    <Card className="cr-conflict-card mb-3" onClick={() => loadConflictDetail(conflict.conflict_id)} style={{ cursor: 'pointer' }}>
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex gap-2 flex-wrap">
            {getSeverityBadge(conflict.severity)}
            <Badge bg="secondary">{conflict.conflict_type}</Badge>
            {conflict.requires_review && <Badge bg="warning">Needs Review</Badge>}
          </div>
          <small className="text-muted">
            <FaClock size={12} className="me-1" />
            {new Date(conflict.detected_at).toLocaleDateString()}
          </small>
        </div>

        <p className="mb-3 text-white-50" style={{ fontSize: '0.9rem' }}>
          {conflict.description}
        </p>

        <Row className="mb-3">
          <Col md={6} className="mb-2 mb-md-0">
            <div className="cr-learning-preview">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="text-info" style={{ fontSize: '0.85rem' }}>Learning 1</strong>
                <div className="d-flex gap-1">
                  <Badge bg="primary" style={{ fontSize: '0.65rem' }}>{conflict.learning_1.learning_type}</Badge>
                  <Badge bg="success" style={{ fontSize: '0.65rem' }}>{conflict.learning_1.classification}</Badge>
                </div>
              </div>
              <div className="cr-text-preview mb-2">
                {conflict.learning_1.text_preview}
              </div>
              <div className="cr-scores-mini d-flex gap-2">
                <small>Trust: <strong>{conflict.learning_1.trust.toFixed(2)}</strong></small>
                <small>Cred: <strong>{conflict.learning_1.credibility.toFixed(2)}</strong></small>
                <small>Imp: <strong>{conflict.learning_1.importance.toFixed(2)}</strong></small>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="cr-learning-preview">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="text-info" style={{ fontSize: '0.85rem' }}>Learning 2</strong>
                <div className="d-flex gap-1">
                  <Badge bg="primary" style={{ fontSize: '0.65rem' }}>{conflict.learning_2.learning_type}</Badge>
                  <Badge bg="success" style={{ fontSize: '0.65rem' }}>{conflict.learning_2.classification}</Badge>
                </div>
              </div>
              <div className="cr-text-preview mb-2">
                {conflict.learning_2.text_preview}
              </div>
              <div className="cr-scores-mini d-flex gap-2">
                <small>Trust: <strong>{conflict.learning_2.trust.toFixed(2)}</strong></small>
                <small>Cred: <strong>{conflict.learning_2.credibility.toFixed(2)}</strong></small>
                <small>Imp: <strong>{conflict.learning_2.importance.toFixed(2)}</strong></small>
              </div>
            </div>
          </Col>
        </Row>

        {conflict.recommendation && (
          <div className="cr-recommendation mb-3">
            <FaInfoCircle size={14} className="me-2" />
            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
              Recommendation: <strong>{conflict.recommendation.suggested.replace(/_/g, ' ')}</strong>
            </span>
            {conflict.similarity && (
              <span className="ms-2 text-muted">
                (Similarity: {(conflict.similarity * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        )}

        {/* Action Buttons Row */}
        <div className="cr-actions-row" onClick={(e) => e.stopPropagation()}>
          <ConflictActionButtons conflict={conflict} />
        </div>
      </Card.Body>
    </Card>
  );

  // Detail Modal
  const DetailModal = ({ conflict, show, onHide }) => {
    if (!conflict) return null;

    return (
      <Modal show={show} onHide={onHide} size="xl" centered className="cr-detail-modal">
        <Modal.Header closeButton className="border-bottom border-secondary">
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaExclamationTriangle className="text-warning" />
            Conflict Resolution
            <Badge bg="secondary" className="ms-2">#{conflict.conflict_id}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Conflict Info */}
          <div className="mb-4">
            <div className="d-flex gap-2 mb-2">
              {getSeverityBadge(conflict.severity)}
              <Badge bg="secondary">{conflict.conflict_type}</Badge>
              <Badge bg="dark">Priority: {conflict.review_priority || 0}</Badge>
            </div>
            <p className="text-white-50">{conflict.description}</p>
            {conflict.metadata && (
              <small className="text-muted">
                Detected: {new Date(conflict.detected_at).toLocaleString()}
              </small>
            )}
          </div>

          {/* Learning 1 */}
          <div className="cr-learning-detail mb-4 p-3 border border-info rounded">
            <h5 className="text-info mb-3">
              Learning 1
              {conflict.recommendation?.recommended_winner_id === conflict.learning_1.learning_id && (
                <Badge bg="success" className="ms-2">Recommended</Badge>
              )}
            </h5>
            <div className="d-flex gap-2 mb-3">
              <Badge bg="primary">{conflict.learning_1.learning_type}</Badge>
              <Badge bg="success">{conflict.learning_1.classification}</Badge>
              <Badge bg="secondary">{conflict.learning_1.status}</Badge>
            </div>
            <div className="cr-text-full mb-3 p-3 bg-dark rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {conflict.learning_1.knowledge_text || conflict.learning_1.text_preview}
            </div>
            <Row>
              <Col md={3}>
                <small className="text-muted d-block">Trust Score</small>
                <strong className="text-info">{conflict.learning_1.trust.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Credibility</small>
                <strong className="text-info">{conflict.learning_1.credibility.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Importance</small>
                <strong className="text-info">{conflict.learning_1.importance.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Access Count</small>
                <strong className="text-info">{conflict.learning_1.access_count}</strong>
              </Col>
            </Row>
            <div className="mt-2">
              <small className="text-muted">
                Source: {conflict.learning_1.source_name || conflict.learning_1.source} 
                (Trust: {conflict.learning_1.source_trust.toFixed(2)})
              </small>
            </div>
          </div>

          {/* Learning 2 */}
          <div className="cr-learning-detail mb-4 p-3 border border-warning rounded">
            <h5 className="text-warning mb-3">
              Learning 2
              {conflict.recommendation?.recommended_winner_id === conflict.learning_2.learning_id && (
                <Badge bg="success" className="ms-2">Recommended</Badge>
              )}
            </h5>
            <div className="d-flex gap-2 mb-3">
              <Badge bg="primary">{conflict.learning_2.learning_type}</Badge>
              <Badge bg="success">{conflict.learning_2.classification}</Badge>
              <Badge bg="secondary">{conflict.learning_2.status}</Badge>
            </div>
            <div className="cr-text-full mb-3 p-3 bg-dark rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {conflict.learning_2.knowledge_text || conflict.learning_2.text_preview}
            </div>
            <Row>
              <Col md={3}>
                <small className="text-muted d-block">Trust Score</small>
                <strong className="text-warning">{conflict.learning_2.trust.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Credibility</small>
                <strong className="text-warning">{conflict.learning_2.credibility.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Importance</small>
                <strong className="text-warning">{conflict.learning_2.importance.toFixed(3)}</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Access Count</small>
                <strong className="text-warning">{conflict.learning_2.access_count}</strong>
              </Col>
            </Row>
            <div className="mt-2">
              <small className="text-muted">
                Source: {conflict.learning_2.source_name || conflict.learning_2.source}
                (Trust: {conflict.learning_2.source_trust.toFixed(2)})
              </small>
            </div>
          </div>

          {/* Recommendation */}
          {conflict.recommendation && (
            <Alert variant="info" className="mb-4">
              <FaChartLine className="me-2" />
              <strong>Recommendation:</strong> {conflict.recommendation.suggested.replace(/_/g, ' ')}
              <br />
              <small>
                Score L1: {conflict.recommendation.score_l1} | Score L2: {conflict.recommendation.score_l2}
                {conflict.similarity && ` | Similarity: ${(conflict.similarity * 100).toFixed(1)}%`}
              </small>
            </Alert>
          )}

          {/* Resolution Notes */}
          <Form.Group className="mb-3">
            <Form.Label className="text-white">Resolution Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Add any notes about this resolution decision..."
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-top border-secondary">
          <Button variant="outline-secondary" onClick={onHide} disabled={resolving}>
            Cancel
          </Button>
          <ButtonGroup>
            <OverlayTrigger overlay={<Tooltip>Dismiss as false positive</Tooltip>}>
              <Button 
                variant="outline-secondary" 
                onClick={() => handleDismiss(conflict.conflict_id)}
                disabled={resolving}
              >
                <FaBan className="me-1" /> Dismiss
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Keep both as complementary</Tooltip>}>
              <Button 
                variant="outline-info" 
                onClick={() => handleBothKeep(conflict.conflict_id)}
                disabled={resolving}
              >
                <FaHandsHelping className="me-1" /> Keep Both
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Escalate for expert review</Tooltip>}>
              <Button 
                variant="outline-warning" 
                onClick={() => handleEscalate(conflict.conflict_id)}
                disabled={resolving}
              >
                <FaArrowUp className="me-1" /> Escalate
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
          <ButtonGroup>
            <OverlayTrigger overlay={<Tooltip>Select Learning 1 as winner</Tooltip>}>
              <Button 
                variant="info" 
                onClick={() => handleResolve(conflict.conflict_id, conflict.learning_1.learning_id)}
                disabled={resolving}
              >
                {resolving ? <Spinner size="sm" /> : <FaGavel className="me-1" />} Choose L1
              </Button>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Select Learning 2 as winner</Tooltip>}>
              <Button 
                variant="warning" 
                onClick={() => handleResolve(conflict.conflict_id, conflict.learning_2.learning_id)}
                disabled={resolving}
              >
                {resolving ? <Spinner size="sm" /> : <FaGavel className="me-1" />} Choose L2
              </Button>
            </OverlayTrigger>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="cr-container">
      {/* Tabs */}
      <div className="cr-tabs mb-3">
        <ButtonGroup>
          <Button
            variant={activeTab === 'dashboard' ? 'primary' : 'outline-secondary'}
            onClick={() => setActiveTab('dashboard')}
          >
            <FaExclamationTriangle className="me-2" /> Active Conflicts
          </Button>
          <Button
            variant={activeTab === 'history' ? 'primary' : 'outline-secondary'}
            onClick={() => setActiveTab('history')}
          >
            <FaHistory className="me-2" /> Resolution History
          </Button>
        </ButtonGroup>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          {/* Stats */}
          {stats && (
            <div className="cr-stats-grid mb-4">
              <StatsCard icon={FaExclamationTriangle} label="Total Conflicts" value={stats.total || 0} color="#ef4444" />
              <StatsCard icon={FaExclamationCircle} label="Unresolved" value={stats.unresolved || 0} color="#f59e0b" />
              <StatsCard icon={FaCheckCircle} label="Resolved" value={stats.resolved || 0} color="#22c55e" />
              <StatsCard icon={FaEye} label="Needs Review" value={stats.needs_review || 0} color="#3b82f6" />
              <StatsCard icon={FaExclamationCircle} label="High Severity" value={stats.high_unresolved || 0} color="#dc2626" />
              <StatsCard icon={FaHandsHelping} label="Both Kept" value={stats.both_kept || 0} color="#06b6d4" />
            </div>
          )}

          {/* Filters */}
          <Card className="mb-3">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 text-white">
                  <FaFilter className="me-2" />
                  Filters
                </h6>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </div>

              {showFilters && (
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="text-muted small">Severity</Form.Label>
                      <Form.Select
                        size="sm"
                        value={filters.severity}
                        onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                        className="bg-dark text-white border-secondary"
                      >
                        <option value="">All</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="text-muted small">Sort By</Form.Label>
                      <Form.Select
                        size="sm"
                        value={filters.sort_by}
                        onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
                        className="bg-dark text-white border-secondary"
                      >
                        <option value="priority">Priority</option>
                        <option value="date">Date</option>
                        <option value="severity">Severity</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="text-muted small">Source</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={filters.source_identifier}
                        onChange={(e) => setFilters({ ...filters, source_identifier: e.target.value })}
                        placeholder="Filter by source..."
                        className="bg-dark text-white border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <ButtonGroup size="sm" className="w-100">
                      <Button variant="primary" onClick={applyFilters}>
                        <FaSearch /> Apply
                      </Button>
                      <Button variant="outline-secondary" onClick={clearFilters}>
                        <FaTimes /> Clear
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Conflicts List */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading conflicts...</p>
            </div>
          ) : conflicts.length === 0 ? (
            <Alert variant="info" className="text-center">
              <FaCheckCircle size={48} className="mb-3 d-block mx-auto" />
              <h5>No Conflicts Found</h5>
              <p className="mb-0">All clear! No unresolved conflicts match your filters.</p>
            </Alert>
          ) : (
            <>
              <div className="mb-3 text-muted small">
                Showing {conflicts.length} of {pagination.total_filtered} conflicts
              </div>
              {conflicts.map(conflict => (
                <ConflictCard key={conflict.conflict_id} conflict={conflict} />
              ))}

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => loadDashboard(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                  >
                    <FaChevronLeft /> Previous
                  </Button>
                  <span className="text-muted">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => loadDashboard(pagination.page + 1)}
                    disabled={pagination.page === pagination.total_pages || loading}
                  >
                    Next <FaChevronRight />
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <Alert variant="info" className="text-center">
              <FaHistory size={48} className="mb-3 d-block mx-auto" />
              <h5>No Resolution History</h5>
              <p className="mb-0">No conflicts have been resolved in the last 30 days.</p>
            </Alert>
          ) : (
            <Card className="kb-card">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-dark table-hover mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Severity</th>
                        <th>Resolution</th>
                        <th>Confidence</th>
                        <th>Auto</th>
                        <th>Resolved By</th>
                        <th>Resolved At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(item => (
                        <tr key={item.conflict_id}>
                          <td><code>{item.conflict_id}</code></td>
                          <td><Badge bg="secondary">{item.type}</Badge></td>
                          <td>{getSeverityBadge(item.severity)}</td>
                          <td>
                            <Badge bg={
                              item.resolution === 'manual_resolution' ? 'success' :
                              item.resolution === 'both_kept' ? 'info' :
                              item.resolution === 'dismissed' ? 'secondary' : 'warning'
                            }>
                              {item.resolution}
                            </Badge>
                          </td>
                          <td>{item.confidence?.toFixed(2) || 'N/A'}</td>
                          <td>
                            {item.auto_resolved ? 
                              <FaCheckCircle className="text-success" /> : 
                              <FaTimes className="text-muted" />
                            }
                          </td>
                          <td className="text-muted small">{item.reviewed_by || 'System'}</td>
                          <td className="text-muted small">
                            {new Date(item.resolved_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* Detail Modal */}
      <DetailModal
        conflict={selectedConflict}
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setResolutionNotes('');
        }}
      />
    </div>
  );
};

export default ConflictResolution;